import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import bookingService from '../../services/bookingService';
import { useCurrencyDisplay } from '../../hooks/useCurrencyDisplay';

const FlightBookingConfirmation = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Currency display hook
  const { getFormattedWithBoth } = useCurrencyDisplay();

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        setLoading(true);
        const response = await bookingService.getBookingConfirmation(bookingId);

        if (response.success) {
          setBookingData(response.data);
        } else {
          setError('Failed to load booking details');
        }
      } catch (err) {
        console.error('Error fetching booking confirmation:', err);
        setError('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) {
      fetchBookingData();
    }
  }, [bookingId]);

  const handleFinish = () => {
    navigate('/jetsetter/profile/booking-history');
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 md:p-8 mt-[5rem] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
          <p className="text-jet-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !bookingData) {
    return (
      <div className="min-h-screen p-4 md:p-8 mt-[5rem] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Booking not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-jet-yellow hover:bg-yellow-500 text-gray-900 font-bold px-8 py-2 rounded transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 mt-[5rem]">
      <div className="w-[86%] mx-auto px-3 sm:px-5">
        {/* Outer White Container - First Div */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Inner Container - Second Div */}
          <div className="p-6">

            {/* Header - Green Bar */}
            <div className="bg-[#58DF57] text-center pt-[0.5rem] pb-[0.25rem] text-bold px-6 mb-6 rounded-[3px]">
              <h1 className="text-xl font-bold text-gray-900">Booking Confirmation</h1>
            </div>

            {/* Flight Details - Conditional Rendering */}
            {bookingData.routeType === 'return' || bookingData.routeType === 'multi_city' ? (
              // Return/Multi-City Flight Display
              <div className="p-4 mb-4 space-y-4">
                {bookingData.flights.map((flight, index) => (
                  <div key={index} className={index < bookingData.flights.length - 1 ? 'pb-4 border-b-2 border-dashed border-gray-300' : ''}>
                    <div className="flex justify-between items-start mb-2">
                      {/* Flight Number Left */}
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-700">{flight.flightNumber}</div>
                        <div className="text-sm font-semibold text-gray-700">{flight.departure.code} {flight.departure.time}</div>
                        <div className="text-sm text-gray-600">{flight.departure.date}</div>
                      </div>

                      {/* Duration Center */}
                      <div className="flex flex-col items-center justify-center flex-1 mx-4">
                        <svg className="w-5 h-5 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        <div className="text-sm text-gray-600">{flight.duration}</div>
                      </div>

                      {/* Flight Number Right */}
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-700">{flight.flightNumber}</div>
                        <div className="text-sm font-semibold text-gray-700">{flight.arrival.code} {flight.arrival.time}</div>
                        <div className="text-sm text-gray-600">{flight.arrival.date}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // One-Way Flight Display (Original)
              <div className="p-4 mb-4">
                <div className="flex justify-between items-start mb-2">
                  {/* Flight Number Left */}
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-700">{bookingData.flightNumber}</div>
                    <div className="text-sm font-semibold text-gray-700">{bookingData.departure.code} {bookingData.departure.time}</div>
                    <div className="text-sm text-gray-600">{bookingData.departure.date}</div>
                  </div>

                  {/* Duration Center */}
                  <div className="flex flex-col items-center justify-center flex-1 mx-4">
                    <svg className="w-5 h-5 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    <div className="text-sm text-gray-600">{bookingData.duration}</div>
                  </div>

                  {/* Flight Number Right */}
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-700">{bookingData.flightNumber}</div>
                    <div className="text-sm font-semibold text-gray-700">{bookingData.arrival.code} {bookingData.arrival.time}</div>
                    <div className="text-sm text-gray-600">{bookingData.departure.date}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Fare Summary and Passengers - Full Width Grid */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">

              {/* Fare Summary */}
              <div>
                <div className="bg-yellow-400 text-gray-900 font-bold px-4 py-2 mb-4 rounded-[3px]">
                  Fare Summary
                </div>

                <div className="space-y-4 text-sm bg-gray-50 p-4">
                  <div className="flex justify-between border-b-2 border-dotted border-gray-200">
                    <span className="text-gray-700">Transaction ID</span>
                    <span className="font-medium text-gray-900 text-right break-all">{bookingData.fareData.transactionId}</span>
                  </div>

                  <div className="flex justify-between border-b-2 border-dotted border-gray-200">
                    <span className="text-gray-700">Reference Number</span>
                    <span className="font-medium text-gray-900">{bookingData.fareData.referenceNumber}</span>
                  </div>

                  <div className="flex justify-between border-b-2 border-dotted border-gray-200">
                    <span className="text-gray-700">Total Passenger(s)</span>
                    <span className="font-bold text-gray-900 text-lg">{bookingData.fareData.totalPassengers}</span>
                  </div>

                  <div className="flex justify-between pt-2">
                    <span className="text-gray-700 font-semibold">Total Fare</span>
                    <span className="font-bold text-gray-900 text-lg">
                      {getFormattedWithBoth(parseFloat(bookingData.fareData.totalFare.replace(/[^0-9.]/g, '')))}
                    </span>
                  </div>
                  {bookingData.isFlightChange && bookingData.fareData.changeSummary && (
                    <>
                      <div className="flex justify-between border-b-2 border-dotted border-gray-200">
                        <span className="text-gray-700 font-semibold">Change Fee</span>
                        <span className="font-bold text-gray-900 text-lg">
                          {getFormattedWithBoth(bookingData.fareData.changeSummary.changeFee)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Passengers */}
              <div>
                <div className="bg-yellow-400 text-gray-900 font-bold px-4 py-2 mb-4 rounded-[3px]">
                  Passenger(s)
                </div>

                <div className="space-y-3 text-sm bg-gray-50 p-4">
                  {bookingData.passengers.map((passenger, index) => (
                    <div key={passenger.id} className="flex items-center border-b-2 border-dotted border-gray-200 last:border-b-0 last:pb-0">
                      <span className="text-gray-700 font-medium mr-4 min-w-fit">{index + 1}</span>
                      <span className="text-gray-900">{passenger.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Finish Button */}
            <div className="flex justify-end">
              <button
                onClick={handleFinish}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-8 py-2 rounded transition-colors"
              >
                Finish
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightBookingConfirmation;

